import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdOutlineDashboard, MdChevronLeft, MdChevronRight, MdRefresh } from 'react-icons/md';
import { BsCalendar2 } from 'react-icons/bs';
import { RxDownload } from 'react-icons/rx';
import { FiFilter } from 'react-icons/fi';
import ComponentWrapper from '../components/utils/ComponentWrapper';
import { useAppSelector, useAppDispatch } from '../redux/hooks';
import toast from 'react-hot-toast';
import Dashboard from '../components/Dashboard/Dashboard';
import Button from '../components/ui/Button/Button';
import { fetchUserProjects, fetchUserProjectsDashboardData } from '../redux/actions/userProjectsAction';
import { loadProjects, fetchDashboardMetrics } from '../redux/actions/projectAction';
import { useUserProjects } from '../hooks/useUserProjects';
import { isAdmin } from '../redux/types/authTypes';

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(state => state.auth);
  const userProjects = useAppSelector(state => state.userProjects);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showProjectToggle, setShowProjectToggle] = useState(false);
  const [projectViewType, setProjectViewType] = useState<'all' | 'my'>('all');
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1), // Default to 1st of current month
    endDate: new Date(), // Default to today
  });
  const [selecting, setSelecting] = useState<'start' | 'end' | null>(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [tempDate, setTempDate] = useState<Date | null>(null);
  const calendarRef = useRef<HTMLDivElement>(null);
  const projectToggleRef = useRef<HTMLDivElement>(null);
  
  // Check if user has permission to read all projects
  const [hasProjectReadPermission, setHasProjectReadPermission] = useState(false);

  // Use the useUserProjects hook to load user projects
  const { isLoading: userProjectsLoading } = useUserProjects();

  // Debug user projects state
  useEffect(() => {
    console.log('[Dashboard] Initial userProjects state:', userProjects);
    console.log('[Dashboard] Current user:', user);

    // Load all projects
    dispatch(loadProjects());

    // Manually try to fetch user projects if user exists
    if (user && user.id) {
      console.log('[Dashboard] Attempting to fetch user projects for user ID:', user.id);
      dispatch(fetchUserProjects({ userId: user.id, page: 1, limit: 10 }))
        .then(result => {
          console.log('[Dashboard] fetchUserProjects result:', result);
        })
        .catch(error => {
          console.error('[Dashboard] fetchUserProjects error:', error);
        });
    }
  }, [user, dispatch]);

  // Check user permissions
  useEffect(() => {
    if (user) {
      // Check if user has projectRead permission
      let hasPermission = false;
      
      // Check if user is admin (always has access)
      if (isAdmin(user)) {
        hasPermission = true;
        console.log('[Dashboard] User is an admin, granting project read permission automatically');
      } 
      // Check permissions object
      else if (user.permissions) {
        // Handle different permission object formats
        if (typeof user.permissions === 'object') {
          if ('projectRead' in user.permissions) {
            hasPermission = Boolean(user.permissions.projectRead);
          }
        }
      }
      
      console.log('[Dashboard] Permission check:', { 
        userRole: user.role, 
        userDesignation: user.designation,
        isAdmin: isAdmin(user),
        hasPermission 
      });
      
      setHasProjectReadPermission(hasPermission);
      
      // If user doesn't have permission to read all projects, default to "my" projects view
      if (!hasPermission) {
        setProjectViewType('my');
      }
      
      console.log('[Dashboard] User permission check:', { 
        hasProjectReadPermission: hasPermission,
        defaultView: !hasPermission ? 'my' : 'all'
      });
    } else {
      // Default to no permission if user is not defined
      setHasProjectReadPermission(false);
      setProjectViewType('my');
    }
  }, [user]);

  // Monitor changes in userProjects
  useEffect(() => {
    console.log('[Dashboard] userProjects state updated:', userProjects);
  }, [userProjects]);

  // Check if user is logged in
  useEffect(() => {
    // If user is null (not loading), redirect to login
    if (user === null) {
      toast.error('Please log in to access this page', {
        duration: 3000,
      });
      navigate('/auth/login');
    }
  }, [user, navigate]);

  // Handle outside clicks to close calendar
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (calendarRef.current && !calendarRef.current.contains(event.target as Node)) {
        setShowDatePicker(false);
      }
      if (projectToggleRef.current && !projectToggleRef.current.contains(event.target as Node)) {
        setShowProjectToggle(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Get days in month for calendar
  const getDaysInMonth = (year: number, month: number) => {
    return new Date(year, month + 1, 0).getDate();
  };

  // Get day of week for first day of month (0 = Sunday, 6 = Saturday)
  const getFirstDayOfMonth = (year: number, month: number) => {
    return new Date(year, month, 1).getDay();
  };

  // Generate calendar days for current month view
  const generateCalendar = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();

    const daysInMonth = getDaysInMonth(year, month);
    const firstDayOfMonth = getFirstDayOfMonth(year, month);

    // Create array with empty slots for days before the first day of month
    const days = Array(firstDayOfMonth).fill(null);

    // Add actual days of month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  // Format date for display
  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Reset date range to first of current month to today
  const resetDateRange = () => {
    const end = new Date(); // Today
    const start = new Date(end.getFullYear(), end.getMonth(), 1); // 1st of current month

    setDateRange({
      startDate: start,
      endDate: end,
    });

    // Reset calendar state
    setSelecting(null);
    setTempDate(null);
    setCurrentMonth(new Date()); // Reset view to current month

    toast.success('Date range reset to current month (1st - today)');
  };

  // Check if a date is the same as another date (ignoring time)
  const isSameDay = (date1: Date, date2: Date) => {
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  };

  // Check if a date is within the selected range
  const isInRange = (date: Date) => {
    if (!date) return false;
    const time = date.getTime();
    const start = dateRange.startDate.getTime();
    const end = dateRange.endDate.getTime();
    return time >= start && time <= end;
  };

  // Handle date click in calendar
  const handleDateClick = (date: Date) => {
    if (!date) return;

    if (!selecting || selecting === 'start') {
      // Starting a new selection
      setTempDate(date);
      setSelecting('end');
    } else if (selecting === 'end') {
      // Completing the selection
      const firstDate = tempDate as Date;

      // Always ensure startDate is earlier than endDate
      if (firstDate.getTime() <= date.getTime()) {
        setDateRange({
          startDate: firstDate,
          endDate: date,
        });
      } else {
        setDateRange({
          startDate: date,
          endDate: firstDate,
        });
      }

      setTempDate(null);
      setSelecting(null);
    }
  };

  // Navigate to previous/next month
  const changeMonth = (increment: number) => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(newMonth.getMonth() + increment);
    setCurrentMonth(newMonth);
  };

  // Handle project view type change
  const handleProjectViewTypeChange = (type: 'all' | 'my') => {
    console.log(`[Dashboard] Changing project view type from ${projectViewType} to ${type}`);
    
    // Update state
    setProjectViewType(type);
    setShowProjectToggle(false);
    
    // Ensure appropriate data is loaded based on the selected view
    const startDateStr = dateRange.startDate.toISOString().split('T')[0];
    const endDateStr = dateRange.endDate.toISOString().split('T')[0];
    
    // If switching to "all", make sure global metrics are up to date
    if (type === 'all') {
      console.log(`[Dashboard] Loading all projects data for date range: ${startDateStr} to ${endDateStr}`);
      dispatch(fetchDashboardMetrics({
        startDate: startDateStr,
        endDate: endDateStr
      }))
      .then(result => {
        console.log('[Dashboard] fetchDashboardMetrics result:', result);
      })
      .catch(error => {
        console.error('[Dashboard] fetchDashboardMetrics error:', error);
        toast.error('Failed to load dashboard data');
      });
    } 
    // If switching to "my", make sure user metrics are up to date
    else if (type === 'my' && user?.id) {
      console.log(`[Dashboard] Loading user projects data for date range: ${startDateStr} to ${endDateStr}`);
      dispatch(fetchUserProjectsDashboardData({
        startDate: startDateStr,
        endDate: endDateStr
      }))
      .then(result => {
        console.log('[Dashboard] fetchUserProjectsDashboardData result:', result);
      })
      .catch(error => {
        console.error('[Dashboard] fetchUserProjectsDashboardData error:', error);
        toast.error('Failed to load user dashboard data');
      });
    }
  };

  // Handle download data
  const handleDownloadData = async () => {
    toast.success(`Downloading project data as Excel file...`);

    try {
      // Import axios instance
      const axiosInstance = (await import('../axios')).default;

      // Make authenticated request to get the Excel file
      const response = await axiosInstance.get('/v1/admin/project/export-excel', {
        responseType: 'blob', // Important for file downloads
      });

      // Create a blob from the response data
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      // Create a temporary URL for the blob
      const url = window.URL.createObjectURL(blob);

      // Create a temporary link element
      const link = document.createElement('a');
      link.href = url;
      link.download = 'projects.xlsx';

      // Append to the document, click it, and remove it
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Release the blob URL
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading data:', error);
      toast.error('There was a problem downloading the data. Please try again.');
    }
  };

  return (
    <ComponentWrapper
      icon={<MdOutlineDashboard size={24} className="text-2xl md:text-3xl" />}
      title="Dashboard"
      buttonsTwo={[
        // Only show project toggle button if user has permission to read all projects
        ...(hasProjectReadPermission ? [{
          component: (
            <div className="relative" ref={projectToggleRef}>
              <Button
                variant="secondary"
                size="sm"
                className="py-2 mr-3 bg-blue-100 hover:bg-blue-200 border-blue-200 text-blue-700"
                icon={<FiFilter size={16} className="text-blue-500" />}
                onClick={() => setShowProjectToggle(!showProjectToggle)}
              >
                {projectViewType === 'all' ? 'All Projects' : 'My Projects'}
              </Button>
              {showProjectToggle && (
                <div className="absolute right-0 mt-1 bg-white border-gray-100 border-2 rounded-lg shadow-lg p-2 z-50 shadow-gray-300/50 w-40">
                  <div 
                    className={`py-2 px-3 rounded cursor-pointer text-sm ${
                      projectViewType === 'all' ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'
                    }`}
                    onClick={() => handleProjectViewTypeChange('all')}
                  >
                    All Projects
                  </div>
                  <div 
                    className={`py-2 px-3 rounded cursor-pointer text-sm ${
                      projectViewType === 'my' ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'
                    }`}
                    onClick={() => handleProjectViewTypeChange('my')}
                  >
                    My Projects
                  </div>
                </div>
              )}
            </div>
          ),
        }] : []),
        {
          component: (
            <Button
              variant="secondary"
              size="sm"
              className="py-2 mr-3 bg-blue-100 hover:bg-blue-200 border-blue-200 text-blue-700"
              icon={<MdRefresh size={16} className="text-blue-500" />}
              onClick={() => {
                toast.loading('Refreshing dashboard...', { id: 'refresh-toast' });
                setTimeout(() => {
                  window.location.reload();
                }, 500);
              }}
            >
              Refresh
            </Button>
          ),
        },
        {
          component: (
            <div className="relative" ref={calendarRef}>
              <Button
                variant="secondary"
                size="sm"
                className="py-2 mr-3 bg-blue-100 hover:bg-blue-200 border-blue-200 text-blue-700"
                icon={<BsCalendar2 size={16} className="text-blue-500" />}
                onClick={() => setShowDatePicker(!showDatePicker)}
              >
                {formatDate(dateRange.startDate)} - {formatDate(dateRange.endDate)}
              </Button>
              {showDatePicker && (
                <div className="absolute right-0 mt-1 bg-white border-gray-100 border-2 rounded-lg shadow-lg p-4 z-50 shadow-gray-300/50 w-80">
                  <div className="flex items-center justify-between mb-4">
                    <button
                      onClick={() => changeMonth(-1)}
                      className="p-1 rounded-full hover:bg-gray-100"
                    >
                      <MdChevronLeft size={20} />
                    </button>
                    <div className="font-medium">
                      {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                    </div>
                    <button
                      onClick={() => changeMonth(1)}
                      className="p-1 rounded-full hover:bg-gray-100"
                    >
                      <MdChevronRight size={20} />
                    </button>
                  </div>

                  {/* Day headers */}
                  <div className="grid grid-cols-7 gap-1 mb-1">
                    {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
                      <div key={day} className="text-center text-xs text-gray-500 font-medium">
                        {day}
                      </div>
                    ))}
                  </div>

                  {/* Calendar days */}
                  <div className="grid grid-cols-7 gap-1">
                    {generateCalendar().map((date, index) => {
                      // Check if it's a temp selected start date
                      const isTempSelected = tempDate && date && isSameDay(date, tempDate);

                      // Check for start/end dates (if not in selection mode)
                      const isStart = !selecting && date && isSameDay(date, dateRange.startDate);
                      const isEnd = !selecting && date && isSameDay(date, dateRange.endDate);

                      // Check if date is within range (only highlight when not selecting)
                      const isWithinRange =
                        !selecting && date && isInRange(date) && !isStart && !isEnd;

                      return (
                        <div
                          key={index}
                          className={`h-8 w-8 flex items-center justify-center text-sm cursor-pointer rounded-full
                            ${date ? 'hover:bg-blue-100' : ''}
                            ${isStart ? 'bg-blue-600 text-white' : ''}
                            ${isEnd ? 'bg-blue-600 text-white' : ''}
                            ${isTempSelected ? 'bg-blue-600 text-white' : ''}
                            ${isWithinRange ? 'bg-blue-100' : ''}
                          `}
                          onClick={() => date && handleDateClick(date)}
                        >
                          {date ? date.getDate() : ''}
                        </div>
                      );
                    })}
                  </div>

                  <div className="mt-4 flex justify-between items-center">
                    <div className="text-xs">
                      {selecting === 'start'
                        ? 'Select start date'
                        : selecting === 'end'
                          ? 'Select end date'
                          : 'Click to select new range'}
                    </div>
                    <button
                      onClick={() => {
                        setSelecting(null);
                        setTempDate(null);
                        setShowDatePicker(false);
                      }}
                      className="bg-blue-600 text-white px-4 py-1 rounded text-sm"
                    >
                      Apply
                    </button>
                    <button
                      onClick={resetDateRange}
                      className="bg-gray-200 text-gray-700 px-4 py-1 rounded text-sm ml-2"
                    >
                      Reset
                    </button>
                  </div>
                </div>
              )}
            </div>
          ),
        },
        {
          component: (
            <Button
              variant="primary"
              size="sm"
              className="py-2 bg-sky-500 hover:bg-sky-700 border-sky-500 hover:border-sky-700"
              icon={<RxDownload size={16} className="text-white" />}
              onClick={handleDownloadData}
            >
              Download Data
            </Button>
          ),
        },
      ]}
    >
      <Dashboard dateRange={dateRange} projectViewType={projectViewType} />
    </ComponentWrapper>
  );
};

export default DashboardPage;
